import { type ClosedReason, ErrorCodes, isAkpError } from '@akpl/shared';

import { setApiErrorHandler } from '../api';
import { getFirebase } from '../firebase';
import { selectCurrent, useRouter } from '../router';
import { useStore } from '../store';
import { resetTvState } from './tvState';

/**
 * Why the session stopped, as the API wrote it into `meta.closedReason` (QA 2026-09-15
 * UX-566). `null` when nothing said: the screen then falls back to the generic copy.
 */
export type SessionEndReason = ClosedReason | null;

/**
 * `401 / 410 session_expired` (docs/03 §1 and §2): the TV token outlived its session, or the
 * host closed it. Any status is accepted for the code — the API answers 401 from the auth
 * middleware and 410 from the session routes.
 */
export function isSessionEndedError(error: unknown): boolean {
  return isAkpError(error) && error.code === ErrorCodes.session_expired;
}

/**
 * Ends the live session (docs/04 §5): local sign-out → TV-11 "La sesión terminó", whose only
 * action goes back to Pairing. Idempotent: the two triggers (RTDB `meta` and the API guard)
 * usually fire within milliseconds of each other.
 *
 * No toast: it used to announce "La sesión terminó" over a screen whose title says exactly
 * that, so the room read the same sentence twice.
 *
 * QA 2026-09-15 UX-566: `reason` is kept in the store (after `clearSession`, which drops
 * everything session-shaped) so TV-11 can say what actually happened instead of "expired or
 * the host closed it" when an operator closed the room from the CMS.
 */
export function endLiveSession(reason: SessionEndReason = null): void {
  const router = useRouter.getState();
  const screen = selectCurrent(router).screen;
  // A session that only ran out of time goes back to Pairing, which reopens the host's room
  // without the QR (`POST /tv/pair/resume`). The API guard (no reason) can win the race and
  // show TV-11 first; the `meta` listener's `expired` then still takes the TV to Pairing.
  if (screen === 'sessionEnded') {
    if (reason === 'expired') router.reset('pairing');
    return;
  }
  if (screen === 'pairing') return;

  const store = useStore.getState();
  resetTvState();
  const firebase = getFirebase();
  if (firebase) {
    void firebase.signOut().catch(() => {
      /* already signed out or offline: the local state below is what gates the shell */
    });
  }

  store.setQueue(null);
  store.setRemotePlayer(null);
  store.resetPlayer();
  store.clearSession();
  store.setEndReason(reason);
  // A dialog left open ("¿Terminar esta canción?") would stay over the session-ended screen and keep
  // the focus, hiding "Pair again".
  store.closeDialog();
  router.reset(reason === 'expired' ? 'pairing' : 'sessionEnded');
}

/**
 * Wires `endLiveSession` to every failed API call. Safe to call more than once.
 *
 * No reason is passed: a `session_expired` answer says the token no longer names a live
 * session, not why — the `meta` listener carries that, and whichever fires first wins.
 */
export function installApiSessionGuard(): void {
  setApiErrorHandler((error) => {
    if (isSessionEndedError(error)) endLiveSession(null);
  });
}
